import { useCallback, useEffect, useMemo, useRef } from 'react';
import { calculateStartingExtremes } from '../functions';
import { TimelineExtremes } from '../shared';
import { useStorageState, UseStorageStateProps, useWindowEventListener } from '@hooks-clean/index';

type SubjectTimelineExtremes = {
    subjectId: number;
    datasetId: number;
    extremes: TimelineExtremes;
};
export const TimelineStartingExtremesStorage: UseStorageStateProps<SubjectTimelineExtremes | null> = {
    key: 'SM:SubjectProfile.SubjectTimelineExtremes',
    defaultValue: null,
    parseFn(value: unknown) {
        if (!(value instanceof Object)) {
            throw Error('Value missing or malformed');
        }

        if (!('subjectId' in value) || typeof value.subjectId !== 'number') {
            throw Error('`subjectId` missing or malformed');
        }
        if (!('datasetId' in value) || typeof value.datasetId !== 'number') {
            throw Error('`datasetId` missing or malformed');
        }

        if (!('extremes' in value) || !(value.extremes instanceof Object)) {
            throw Error('`extremes` missing or malformed');
        }
        if (!('min' in value.extremes) || typeof value.extremes.min !== 'number') {
            throw Error('extremes.min` missing or malformed');
        }
        if (!('max' in value.extremes) || typeof value.extremes.max !== 'number') {
            throw Error('`extremes.max` missing or malform');
        }

        return value as SubjectTimelineExtremes;
    },
    storageApi: sessionStorage,
    equalityComparer(a, b) {
        return (
            a?.subjectId === b?.subjectId &&
            a?.datasetId === b?.datasetId &&
            a?.extremes.min === b?.extremes.min &&
            a?.extremes.max === b?.extremes.max
        );
    },
};

type Props = {
    datasetId: number;
    subjectId: number;
    startDate: string;
    endDate: string | null;
    datasetCutDate: Date;
};
/**
 * Persists extremes value for a dataset-subject combo. Value is persisted between unmounting and page loads.
 *
 * Value is discarded when `datasetId` or `subjectId` changes.
 */
export function usePersistentStartingExtremes({ datasetId, subjectId, startDate, endDate, datasetCutDate }: Props) {
    const defaultExtremes = useMemo(() => {
        if (endDate == null) {
            return null;
        }

        return calculateStartingExtremes({
            start: new Date(startDate).getTime(),
            end: new Date(endDate).getTime(),
            target: datasetCutDate.getTime(),
            duration: 28 * 24 * 60 * 60 * 1000, // 28 days
        });
    }, [startDate, endDate, datasetCutDate]);

    const [startingExtremes, setStartingExtremes, deleteStartingExtremes] = useStorageState(
        TimelineStartingExtremesStorage,
    );

    // Track changes to extremes in a ref - Using state would cause a rerender,
    // and rerendering while the navigator extremes are being dragged makes the navigator vanish, for some reason...
    const extremesRef = useRef<TimelineExtremes>(null);

    const setExtremes = useCallback((extremes: TimelineExtremes) => {
        extremesRef.current = extremes;
    }, []);

    // Instead, only update value in storage on unmount/unload
    const updateStartingExtremesForSubject = useCallback(
        (subjectIdArg: number, datasetIdArg: number) => {
            setStartingExtremes(
                extremesRef.current != null
                    ? {
                          subjectId: subjectIdArg,
                          datasetId: datasetIdArg,
                          extremes: extremesRef.current,
                      }
                    : null,
            );
        },
        [setStartingExtremes],
    );

    useEffect(
        () => () => {
            updateStartingExtremesForSubject(subjectId, datasetId);
        },
        [subjectId, datasetId, updateStartingExtremesForSubject],
    );
    useWindowEventListener('unload', () => {
        updateStartingExtremesForSubject(subjectId, datasetId);
    });

    // Determine if stored value (if exists) is for a different dataset-subject combo
    const storedValueForDifferentSubjectOrDataset =
        startingExtremes != null &&
        (startingExtremes.subjectId !== subjectId || startingExtremes.datasetId !== datasetId);

    // If stored extremes are not for the current dataset-subject combo, delete them.
    if (storedValueForDifferentSubjectOrDataset) {
        deleteStartingExtremes();
    }

    // Prevent stored extremes being returned for the wrong dataset-subject combo
    const resolvedStartingExtremes = !storedValueForDifferentSubjectOrDataset
        ? (startingExtremes?.extremes ?? defaultExtremes)
        : defaultExtremes;

    return useMemo(
        () => ({
            startingExtremes: resolvedStartingExtremes,
            extremesRef,
            setExtremes,
        }),
        [resolvedStartingExtremes, setExtremes],
    );
}
